import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import '../styles/Videos.css';

/* ═══════════════════════════════════════════════════════════
   VIDEOS DATA — YouTube placeholder structure
   Replace videoId values with actual YouTube IDs when available
   ═══════════════════════════════════════════════════════════ */

const videoCategories = [
  { id: 'all', label: 'الكل' },
  { id: 'recitation', label: 'قراءات شعرية' },
  { id: 'interview', label: 'لقاءات ومقابلات' },
  { id: 'event', label: 'أمسيات وفعاليات' },
  { id: 'documentary', label: 'وثائقيات' },
];

const videos = [
  /* ── RECITATIONS ──────────────────────────────── */
  {
    id: 'v1',
    title: 'قصيدة وجع الأرض .. عرس الوطن',
    description: 'وجدي منيف عبد الصمد يلقي قصيدته الوطنية الرائعة في حفل أدبي',
    category: 'recitation',
    videoId: 'PLACEHOLDER_1',            // ← Replace with real YouTube ID
    duration: '5:32',
    year: 2024,
    featured: true,
  },
  {
    id: 'v2',
    title: 'قصيدة طوفان الأقصى',
    description: 'إلقاء حيّ من أمسية شعرية في لبنان',
    category: 'recitation',
    videoId: 'PLACEHOLDER_2',
    duration: '4:18',
    year: 2024,
    featured: false,
  },
  {
    id: 'v3',
    title: 'قصيدة لبنان كما نحلم به',
    description: 'من ديوان "لم يبقَ لي غير الكلمات"',
    category: 'recitation',
    videoId: 'PLACEHOLDER_3',
    duration: '6:05',
    year: 2024,
    featured: false,
  },
  {
    id: 'v4',
    title: 'قصيدة صاحبة الجلالة أمّي',
    description: 'قصيدة مهداة إلى الأمهات في يوم الأم',
    category: 'recitation',
    videoId: 'PLACEHOLDER_4',
    duration: '3:47',
    year: 2023,
    featured: false,
  },
  /* ── INTERVIEWS ───────────────────────────────── */
  {
    id: 'v5',
    title: 'لقاء تلفزيوني — الشاعر ومسيرته',
    description: 'حوار شامل حول تجربته الشعرية وإصداراته الأخيرة',
    category: 'interview',
    videoId: 'PLACEHOLDER_5',
    duration: '22:10',
    year: 2024,
    featured: true,
  },
  {
    id: 'v6',
    title: 'مقابلة — الشعر العربي في المهجر',
    description: 'رؤيته في الحفاظ على الشعر الكلاسيكي',
    category: 'interview',
    videoId: 'PLACEHOLDER_6',
    duration: '15:30',
    year: 2022,
    featured: false,
  },
  /* ── EVENTS ───────────────────────────────────── */
  {
    id: 'v7',
    title: 'مؤتمر الشعر العربي — أبو ظبي 2019',
    description: 'مشاركة في مؤتمر الشعر العربي، أبو ظبي',
    category: 'event',
    videoId: 'PLACEHOLDER_7',
    duration: '18:44',
    year: 2019,
    featured: true,
  },
  {
    id: 'v8',
    title: 'مهرجان الشعر الآسيوي — كازاخستان 2019',
    description: 'تمثيل لبنان في مهرجان الشعر الآسيوي الدولي',
    category: 'event',
    videoId: 'PLACEHOLDER_8',
    duration: '12:22',
    year: 2019,
    featured: false,
  },
  {
    id: 'v9',
    title: 'يوم الشعر في بعقلين',
    description: 'أمسية شعرية في بلدة بعقلين — الشوف',
    category: 'event',
    videoId: 'PLACEHOLDER_9',
    duration: '40:10',
    year: 2023,
    featured: false,
  },
  /* ── DOCUMENTARY ──────────────────────────────── */
  {
    id: 'v10',
    title: 'وثائقي — الشاعر من عماطور',
    description: 'رحلة وجدي من عماطور الشوف إلى أستراليا وعودته إلى الكلمة',
    category: 'documentary',
    videoId: 'PLACEHOLDER_10',
    duration: '28:55',
    year: 2023,
    featured: false,
  },
];

/* ═══════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════ */

// Build YouTube thumbnail — returns null for non-YouTube videos
const getThumbnail = (video) => {
  // YouTube videos with a real ID get a thumbnail from YouTube
  if (video.videoId && !video.videoId.startsWith('PLACEHOLDER')) {
    return `https://img.youtube.com/vi/${video.videoId}/maxresdefault.jpg`;
  }
  return null; // non-YouTube cards use platform icon fallback
};

// YouTube embed URL
const getEmbedUrl = (videoId) =>
  `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;

// Platform icon + label for non-YouTube video cards
const getPlatformInfo = (videoType) => {
  switch (videoType) {
    case 'instagram': return { icon: '📷', label: 'Instagram', color: '#E4405F' };
    case 'facebook':  return { icon: '📘', label: 'Facebook', color: '#1877F2' };
    case 'tiktok':    return { icon: '🎵', label: 'TikTok', color: '#69C9D0' };
    default:          return { icon: '▶', label: 'فيديو', color: '#C4A44A' };
  }
};

/* ═══════════════════════════════════════════════════════════
   INSTAGRAM EMBED — loads Instagram's embed.js script
   Renders the actual IG post with video player inline
   ═══════════════════════════════════════════════════════════ */
function InstagramEmbed({ url }) {
  const containerRef = useRef(null);

  useEffect(() => {
    // Clean the URL — strip query params, ensure trailing slash
    let cleanUrl = url.split('?')[0];
    if (!cleanUrl.endsWith('/')) cleanUrl += '/';

    // Build the blockquote that Instagram's embed.js scans for
    if (containerRef.current) {
      containerRef.current.innerHTML = `
        <blockquote
          class="instagram-media"
          data-instgrm-permalink="${cleanUrl}"
          data-instgrm-version="14"
          style="max-width:540px; width:100%; margin:0 auto; background:#111; border:0;">
        </blockquote>
      `;

      // If the script is already loaded, just re-process; otherwise load it
      if (window.instgrm) {
        window.instgrm.Embeds.process();
      } else {
        const script = document.createElement('script');
        script.src = 'https://www.instagram.com/embed.js';
        script.async = true;
        script.onload = () => {
          if (window.instgrm) window.instgrm.Embeds.process();
        };
        document.body.appendChild(script);
      }
    }
  }, [url]);

  return <div ref={containerRef} style={{ width: '100%', maxWidth: 540, margin: '0 auto', padding: '1rem 0' }} />;
}

/* ═══════════════════════════════════════════════════════════
   FACEBOOK EMBED — uses FB video embed plugin iframe
   ═══════════════════════════════════════════════════════════ */
function FacebookEmbed({ url }) {
  const encodedUrl = encodeURIComponent(url);
  return (
    <iframe
      src={`https://www.facebook.com/plugins/video.php?href=${encodedUrl}&show_text=false&width=560`}
      width="560"
      height="315"
      style={{ border: 'none', overflow: 'hidden', maxWidth: '100%' }}
      scrolling="no"
      frameBorder="0"
      allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
      allowFullScreen
      title="Facebook Video"
    />
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════ */
export default function Videos() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [allVideos, setAllVideos] = useState(videos);
  const [playingId, setPlayingId] = useState(null);
  const [modalVideo, setModalVideo] = useState(null);
  const [revealed, setRevealed] = useState(new Set());
  const sectionRef = useRef(null);
  const cardRefs = useRef({});

  /* ── Fetch videos from Supabase ──────────────────── */
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const { data, error } = await supabase
          .from('videos')
          .select('*')
          .order('sort_order', { ascending: true });

        if (error) throw error;

        if (data && data.length > 0) {
          setAllVideos(data.map(v => ({
            id: v.id,
            title: v.title,
            description: v.description,
            category: v.category,
            videoId: v.youtube_id || null,
            videoType: v.video_type || 'youtube',
            videoUrl: v.video_url || null,
            duration: v.duration,
            year: v.year,
            featured: v.is_featured,
          })));
        }
      } catch (_) {
        // Keep fallback videos
      }
    };

    fetchVideos();

    const channel = supabase
      .channel('videos_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'videos' }, () => {
        fetchVideos();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  /* ── Scroll-reveal ──────────────────────────────── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setRevealed((prev) => new Set([...prev, entry.target.dataset.id]));
          }
        });
      },
      { threshold: 0.12 }
    );

    Object.values(cardRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [activeCategory]);

  /* ── Close modal on Escape ──────────────────────── */
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setModalVideo(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* ── Filtered list ──────────────────────────────── */
  const filtered = activeCategory === 'all'
    ? allVideos
    : allVideos.filter((v) => v.category === activeCategory);

  const featured = allVideos.find((v) => v.featured && (activeCategory === 'all' || v.category === activeCategory));
  const rest = filtered.filter((v) => v.id !== featured?.id);

  // A video is a placeholder ONLY if it has no YouTube ID AND no video URL
  // Instagram/Facebook/TikTok videos have videoUrl but no videoId — they're real content
  const isPlaceholder = (v) => {
    if (v.videoUrl) return false;                              // has a direct URL → real video
    if (v.videoType === 'uploaded' && v.videoUrl) return false; // uploaded file → real
    return !v.videoId || v.videoId.startsWith('PLACEHOLDER');   // YouTube placeholder check
  };

  const handlePlay = (video) => {
    if (isPlaceholder(video) && video.videoType !== 'uploaded' && !video.videoUrl) return;
    setModalVideo(video);
  };

  return (
    <section id="videos" className="videos-section" ref={sectionRef} dir="rtl">
      {/* Ambient background */}
      <div className="videos-bg-glow" aria-hidden="true" />

      <div className="videos-container">

        {/* ── Header ───────────────────────────────── */}
        <header className="videos-header">
          <span className="videos-label">VIDEOS</span>
          <h2 className="videos-title">مرئيات وتسجيلات</h2>
          <div className="ornament-row" aria-hidden="true">
            <span className="ornament-line-v" />
            <span className="ornament-diamond">◆</span>
            <span className="ornament-line-v" />
          </div>
          <p className="videos-subtitle">
            قراءات شعرية، لقاءات، أمسيات، ومقابلات مع الشاعر وجدي منيف عبد الصمد
          </p>
        </header>

        {/* ── Category Filter ───────────────────────── */}
        <nav className="videos-filter" aria-label="فلتر الفيديوهات">
          {videoCategories.map((cat) => (
            <button
              key={cat.id}
              className={`filter-btn${activeCategory === cat.id ? ' active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
              aria-pressed={activeCategory === cat.id}
            >
              {cat.label}
            </button>
          ))}
        </nav>

        {/* ── Featured Video ─────────────────────────── */}
        {featured && (
          <div
            className={`featured-video${revealed.has('featured') ? ' revealed' : ''}`}
            ref={(el) => { cardRefs.current['featured'] = el; }}
            data-id="featured"
          >
            <div
              className={`featured-thumb${isPlaceholder(featured) ? ' is-placeholder' : ''}`}
              onClick={() => handlePlay(featured)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handlePlay(featured)}
              aria-label={`تشغيل: ${featured.title}`}
            >
              {/* ── Thumbnail logic: YouTube → img, Social → platform icon, Placeholder → coming soon ── */}
              {!isPlaceholder(featured) ? (
                getThumbnail(featured) ? (
                  // YouTube video — show YouTube thumbnail
                  <img
                    src={getThumbnail(featured)}
                    alt={featured.title}
                    loading="lazy"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  // Social platform video (Instagram/Facebook/TikTok) — show branded icon
                  <div className="placeholder-thumb" style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #111 100%)' }}>
                    <span className="placeholder-icon" style={{ fontSize: '3rem' }}>{getPlatformInfo(featured.videoType).icon}</span>
                    <span className="placeholder-text" style={{ fontSize: '.85rem', opacity: .7 }}>
                      {getPlatformInfo(featured.videoType).label}
                    </span>
                  </div>
                )
              ) : (
                // Actual placeholder — no video added yet
                <div className="placeholder-thumb">
                  <span className="placeholder-icon">🎬</span>
                  <span className="placeholder-text">سيُضاف الفيديو قريباً</span>
                </div>
              )}

              <div className="featured-overlay">
                <div className="play-btn-large" aria-hidden="true">
                  <svg viewBox="0 0 60 60" fill="none">
                    <circle cx="30" cy="30" r="29" stroke="currentColor" strokeWidth="1.5" />
                    <polygon points="23,18 47,30 23,42" fill="currentColor" />
                  </svg>
                </div>
              </div>

              {featured.featured && (
                <span className="badge-featured">مميّز</span>
              )}
              <span className="badge-duration">{featured.duration}</span>
            </div>

            <div className="featured-info">
              <span className="featured-category-tag">
                {videoCategories.find((c) => c.id === featured.category)?.label}
              </span>
              <h3 className="featured-title">{featured.title}</h3>
              <p className="featured-desc">{featured.description}</p>
              <div className="featured-meta">
                <span className="meta-year">{featured.year}</span>
                <span className="meta-sep">·</span>
                <span className="meta-dur">{featured.duration}</span>
              </div>
              <button
                className={`watch-btn${isPlaceholder(featured) ? ' disabled' : ''}`}
                onClick={() => handlePlay(featured)}
                disabled={isPlaceholder(featured)}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
                {isPlaceholder(featured) ? 'قريباً' : 'شاهد الآن'}
              </button>
            </div>
          </div>
        )}

        {/* ── Video Grid ────────────────────────────── */}
        {rest.length > 0 && (
          <div className="videos-grid">
            {rest.map((video, idx) => (
              <article
                key={video.id}
                className={`video-card${revealed.has(video.id) ? ' revealed' : ''}`}
                ref={(el) => { cardRefs.current[video.id] = el; }}
                data-id={video.id}
                style={{ '--delay': `${idx * 80}ms` }}
              >
                <div
                  className={`video-thumb${isPlaceholder(video) ? ' is-placeholder' : ''}`}
                  onClick={() => handlePlay(video)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handlePlay(video)}
                  aria-label={`تشغيل: ${video.title}`}
                >
                  {/* ── Thumbnail: YouTube → img, Social → platform icon, Placeholder → ▶ ── */}
                  {!isPlaceholder(video) ? (
                    getThumbnail(video) ? (
                      // YouTube video
                      <img
                        src={getThumbnail(video)}
                        alt={video.title}
                        loading="lazy"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      // Social platform video — branded icon in the thumbnail
                      <div className="placeholder-thumb-sm" style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #111 100%)' }}>
                        <span className="placeholder-icon-sm" style={{ fontSize: '2rem' }}>{getPlatformInfo(video.videoType).icon}</span>
                      </div>
                    )
                  ) : (
                    // Actual placeholder
                    <div className="placeholder-thumb-sm">
                      <span className="placeholder-icon-sm">▶</span>
                    </div>
                  )}

                  <div className="thumb-overlay">
                    <div className="play-btn-sm" aria-hidden="true">
                      <svg viewBox="0 0 40 40" fill="none">
                        <circle cx="20" cy="20" r="19" stroke="currentColor" strokeWidth="1.5" />
                        <polygon points="15,12 31,20 15,28" fill="currentColor" />
                      </svg>
                    </div>
                  </div>

                  <span className="card-duration">{video.duration}</span>
                </div>

                <div className="video-card-info">
                  <span className="card-category">
                    {videoCategories.find((c) => c.id === video.category)?.label}
                  </span>
                  <h4 className="card-title">{video.title}</h4>
                  <p className="card-desc">{video.description}</p>
                  <span className="card-year">{video.year}</span>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* ── Empty state ───────────────────────────── */}
        {filtered.length === 0 && (
          <div className="videos-empty">
            <span className="empty-icon">🎞</span>
            <p>لا توجد تسجيلات في هذا التصنيف حتى الآن</p>
          </div>
        )}

        {/* ── Dev note for placeholder state ── */}
        {filtered.every(isPlaceholder) && (
          <div className="videos-cta-note">
            <p>
              <strong>ملاحظة للمطوّر:</strong> أضف روابط YouTube الحقيقية في لوحة التحكم <code>/admin</code>
            </p>
          </div>
        )}

      </div>

      {/* ── Modal Player ─────────────────────────────── */}
      {modalVideo && (
        <div
          className="video-modal-overlay"
          onClick={() => setModalVideo(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`تشغيل فيديو: ${modalVideo.title}`}
        >
          <div
            className="video-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close-btn"
              onClick={() => setModalVideo(null)}
              aria-label="إغلاق"
            >
              ✕
            </button>
            <div className="modal-iframe-wrap">
              {/* ── YouTube ── */}
              {modalVideo.videoType === 'youtube' && modalVideo.videoId && (
                <iframe
                  src={getEmbedUrl(modalVideo.videoId)}
                  title={modalVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
              {/* ── Uploaded video file ── */}
              {modalVideo.videoType === 'uploaded' && modalVideo.videoUrl && (
                <video
                  src={modalVideo.videoUrl}
                  controls
                  autoPlay
                  style={{ width: '100%', height: '100%', position: 'absolute', inset: 0, objectFit: 'contain', background: '#000' }}
                />
              )}
              {/* ── Instagram — actual embed via embed.js script ── */}
              {modalVideo.videoType === 'instagram' && modalVideo.videoUrl && (
                <div style={{ position: 'absolute', inset: 0, overflow: 'auto', background: '#111', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '1rem 0' }}>
                  <InstagramEmbed url={modalVideo.videoUrl} />
                </div>
              )}
              {/* ── Facebook — uses FB video embed plugin iframe ── */}
              {modalVideo.videoType === 'facebook' && modalVideo.videoUrl && (
                <div style={{ position: 'absolute', inset: 0, overflow: 'auto', background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FacebookEmbed url={modalVideo.videoUrl} />
                </div>
              )}
              {/* ── TikTok — no reliable embed, open externally ── */}
              {modalVideo.videoType === 'tiktok' && modalVideo.videoUrl && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', background: '#111' }}>
                  <p style={{ color: '#9A8E7A', fontFamily: 'Tajawal, sans-serif', fontSize: '.95rem' }}>
                    🎵 فيديو TikTok
                  </p>
                  <a
                    href={modalVideo.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ padding: '.75rem 2rem', background: '#C4A44A', color: '#0A0A0A', borderRadius: '10px', fontFamily: 'Tajawal, sans-serif', fontWeight: 700, textDecoration: 'none', fontSize: '.9rem' }}
                  >
                    مشاهدة الفيديو ←
                  </a>
                </div>
              )}
              {/* ── Fallback for old placeholder videos ── */}
              {(!modalVideo.videoType || modalVideo.videoType === 'youtube') && !modalVideo.videoId && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#111', color: '#9A8E7A', fontFamily: 'Tajawal, sans-serif' }}>
                  سيُضاف الفيديو قريباً
                </div>
              )}
            </div>
            <div className="modal-info">
              <h3>{modalVideo.title}</h3>
              <p>{modalVideo.description}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}